import api from "./api";

export type TEstablishmentFilterOption = {
  id: string;
  name: string;
  livePlaylistId: number | null;
};

const toRows = (payload: any): any[] => {
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.establishments)) return payload.establishments;
  if (Array.isArray(payload)) return payload;
  return [];
};

const parseId = (row: any): string => {
  return String(
    row?.establishmentId ??
      row?.id ??
      row?.uuid ??
      row?.placeUuid ??
      row?.establishment_id ??
      "",
  ).trim();
};

const parseName = (row: any, fallbackId: string): string => {
  return String(
    row?.name ?? row?.establishmentName ?? row?.placeName ?? fallbackId,
  ).trim();
};

const parseLivePlaylistId = (row: any): number | null => {
  const raw =
    row?.livePlaylistId ??
    row?.livePlaylistID ??
    row?.playlistId ??
    row?.live_playlist_id ??
    row?.livePlaylist?.id ??
    row?.live_playlist?.id;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const upsertOption = (
  map: Map<string, TEstablishmentFilterOption>,
  id: string,
  name: string,
  livePlaylistId: number | null,
) => {
  const current = map.get(id);
  if (!current) {
    map.set(id, { id, name: name || id, livePlaylistId });
    return;
  }
  map.set(id, {
    id,
    name: current.name || name || id,
    livePlaylistId:
      typeof current.livePlaylistId === "number"
        ? current.livePlaylistId
        : livePlaylistId,
  });
};

const sortOptions = (rows: TEstablishmentFilterOption[]) =>
  rows.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );

export async function fetchEstablishmentsForFilters(args?: {
  country?: string;
  skipPrimaryEstablishmentsEndpoint?: boolean;
}): Promise<TEstablishmentFilterOption[]> {
  const map = new Map<string, TEstablishmentFilterOption>();

  let primaryError: any = null;
  if (!args?.skipPrimaryEstablishmentsEndpoint) {
    try {
      const pageSize = 200;
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await api.get("/app/establishments", {
          params: {
            limit: pageSize,
            offset,
            ...(args?.country ? { country: args.country } : {}),
          },
        });
        const payload = response.data as any;
        const rows = toRows(payload);

        for (const row of rows) {
          const id = parseId(row);
          if (!id) continue;
          const name = parseName(row, id);
          const livePlaylistId = parseLivePlaylistId(row);
          upsertOption(map, id, name, livePlaylistId);
        }

        const total = Number(
          payload?.count ??
            payload?.total ??
            payload?.totalCount ??
            payload?.total_count,
        );
        const nextOffset = offset + rows.length;
        const reachedTotal =
          Number.isFinite(total) && total >= 0 ? nextOffset >= total : false;
        hasMore = rows.length === pageSize && !reachedTotal;
        offset = nextOffset;
      }
    } catch (error) {
      primaryError = error;
    }
  }

  if (map.size > 0) return sortOptions(Array.from(map.values()));

  // Fallback for profiles that cannot access /app/establishments (e.g., OWNER)
  try {
    const statusRes = await api.get<{ data?: any[] }>("/app/cameras/status");
    const statusRows = toRows(statusRes.data);
    for (const row of statusRows) {
      const id = String(row?.establishmentId ?? "").trim();
      if (!id) continue;
      const name = String(row?.establishmentName ?? id).trim();
      upsertOption(map, id, name || id, null);
    }
  } catch {
    // ignore fallback errors
  }

  try {
    const templatesRes = await api.get<{ data?: any[] }>("/app/lives/templates");
    const templateRows = toRows(templatesRes.data);
    for (const row of templateRows) {
      const id = String(row?.placeUuid ?? "").trim();
      if (!id) continue;
      const name = String(row?.placeName ?? id).trim();
      upsertOption(map, id, name || id, null);
    }
  } catch {
    // ignore fallback errors
  }

  const fallbackRows = sortOptions(Array.from(map.values()));
  if (fallbackRows.length > 0) return fallbackRows;
  if (primaryError) return [];
  return [];
}

/**
 * Busca uma única página de estabelecimentos (server-side `search` por nome), para filtros
 * tipo Autocomplete. Diferente de {@link fetchEstablishmentsForFilters}, nunca pagina a lista
 * inteira.
 */
const SEARCH_CACHE_TTL_MS = 30_000;
const searchCache = new Map<
  string,
  { at: number; rows: TEstablishmentFilterOption[] }
>();

export async function searchEstablishmentsForFilters(args?: {
  country?: string;
  search?: string;
  limit?: number;
  signal?: AbortSignal;
}): Promise<TEstablishmentFilterOption[]> {
  const search = args?.search?.trim();
  const cacheKey = `${args?.country ?? ""}|${search ?? ""}|${args?.limit ?? 30}`;
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() - cached.at < SEARCH_CACHE_TTL_MS) return cached.rows;
  const response = await api.get("/app/establishments", {
    params: {
      limit: args?.limit ?? 30,
      offset: 0,
      ...(args?.country ? { country: args.country } : {}),
      ...(search ? { search } : {}),
    },
    signal: args?.signal,
  });
  const map = new Map<string, TEstablishmentFilterOption>();
  for (const row of toRows(response.data)) {
    const id = parseId(row);
    if (!id) continue;
    upsertOption(map, id, parseName(row, id), parseLivePlaylistId(row));
  }
  const rows = sortOptions(Array.from(map.values()));
  searchCache.set(cacheKey, { at: Date.now(), rows });
  return rows;
}
